import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, makeStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { invokeGetService, invokePostService, toggleDeployment, toggleValidator, updateLogRedirectionData } from '../../../store/global/actions';

import { COLORS, SRC } from './logsConstants';
import LogsHeader from './LogsHeader';
import RuntimeConfigPanel from './RuntimeConfigPanel';
import LogsToolbar from './LogsToolbar';
import LogsTable from './LogsTable';
import { getDetailsFromUrl } from '../../../services/blueprint';
import useDebounce from '../../../hooks/useDebounce';
import socket from '../../../websocket';
import { EVENTS } from '../../../websocket/events';
import { getLogRedirectionData, getValidationTrigger, getSimulationRunStatus } from '../../../store/global/selectors';
import { getTimeAgo } from '../../../utils/commonUtils';
import LastRefreshedTime from './LastRefreshedTime';


const useStyles = makeStyles(() => ({
  paper: {
    width: '100%',
    maxWidth: 1500,
    borderRadius: 8,
    position: 'relative',
    zIndex: 100,
    height: '100%'
  },
  resultLine: {
    fontSize: '12.5px',
    color: COLORS.textSecondary,
    padding: '7px 24px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    minHeight: 48,
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  
  refreshInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#757575',
    fontSize: '12px',
  },
  
  refreshLabel: {
    color: '#757575',
  },
  
  refreshTime: {
    color: '#9e9e9e',
  },
  
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginLeft: 'auto',
  },
  
  cancelButton: {
    fontSize: '12.5px',
    border: '1px solid #d0d0d0',
    borderRadius: 6,
    backgroundColor: '#fff',
    color: '#333',
    padding: '6px 14px',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
  
    '&:hover': {
      backgroundColor: '#f7f7f7',
      borderColor: '#bdbdbd',
    },
  },
  
  proceedButton: {
    fontSize: '12.5px',
    border: '1px solid #3f4e5c',
    borderRadius: 6,
    backgroundColor: '#3f4e5c',
    color: '#fff',
    padding: '6px 14px',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'background-color 0.15s ease',
  
    '&:hover': {
      backgroundColor: '#34414d',
    },
  },
}));

export function computeNextSources(prevSources, clicked) {
  if (clicked === 'Blueprint Validator') {
    return new Set(['Blueprint Validator']);
  }
  if (prevSources.has('Blueprint Validator')) {
    return new Set([clicked]);
  }
  const next = new Set(prevSources);
  if (next.has(clicked)) {
    if (next.size > 1) next.delete(clicked); // never allow zero selected
  } else {
    next.add(clicked);
  }
  return next;
}

export default function LogsModal({
  open,
  onClose,
  logMap,
  setLogsCount,
  isSimulationRunning,
  validatorStatus
}) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { screen, entityId, blueprintId } = getDetailsFromUrl();
  const triggerValidation = useSelector(getValidationTrigger);
  const simulationRunning = useSelector(getSimulationRunStatus)

  const logRedirectionData = useSelector(getLogRedirectionData)

  // Falls back to the bundled mock dataset until the real API call resolves.
  const [logs, setLogs] = useState([]);
  const [validatorLogs, setValidatorLogs] = useState([])
  const [restarting, setRestarting] = useState(false);
  const [refreshSpin, setRefreshSpin] = useState(false);
  const [level, setLevel] = useState([])
  const [debugLevel, setDebugLevel] = useState('INFO');
  const [logLevel, setLogLevel] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search,400)
  const [time, setTime] = useState('all');
  const [activeSources, setActiveSources] = useState(() => new Set([]));
  const [ssliceInfo, setSsliceInfo] = useState(null);
  const [tableData, setTableData] = useState([])
  const [isLoading, setLoading] = useState(false);
  const [selectedSimulationRun, setSelectedSimulationRun] = useState(null)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [justPostSimulation, setJustPostSimulation] = useState(false)
  const [isClearingRuntimeLogs, setClearingRuntimeLogs] = useState(false);
  const [lastRefreshedTime, setLastRefreshedTime] = useState(null)

  const showTimeFilter = useMemo(() => {
    if(activeSources) {
      return !activeSources.has('Blueprint Validator')
    }else {
      return false
    }
  },[activeSources]);

  const counts = useMemo(() => {
    const c = {};
    for(let s of Object.keys(SRC)){
      if(s === "Blueprint Validator"){
        c[s] = validatorLogs.length;
      }else if(s === "Runtime"){
        c[s] = logs?.filter((l) => l.src === s).length || 0;
      }
    }
    setLogsCount(c);
    return c;
  }, [logs, validatorLogs, setLogsCount]);

  const fetchValidations = useCallback(async (search = "", level = []) => {
    setLoading(true);
    try {
      dispatch(invokeGetService('validateBlueprint', { search, level })).then(response => {
        if (response.status) {
          if (response.data) {
            const errors = response.data?.errors || [];
            const warnings = response.data?.warnings || [];
            const validations = [...errors, ...warnings];
            setValidatorLogs(validations.map(item => ({
              ...item,
              msg: item.message,
              level: item.severity,
              src: "Validator"
            })));
            
            if(logRedirectionData?.source === "Validator"){
              let foundItem = validations.find(item => item.message === logRedirectionData.message);
              if(!foundItem){
                dispatch(updateLogRedirectionData(null))
              }
              console.log({logRedirectionData, validations, foundItem})
            } 
          } else {
            console.log("--No validation data found--");
          }
        } else {
          throw new Error('Fetching validations failed.');
        }
        setLoading(false);
      }).catch(error => {
        console.error(`Fetching validations failed. ${error}`);
      });
    } catch (err) {
      console.error("Error fetching validations:", err);
    }
  },[dispatch, logRedirectionData]);

  const fetchLogs = useCallback(async(level, search, from, to) => {
    try {
      const result = await dispatch(invokePostService('runtimeLogs', {logMap, level, search, from, to}, {  }, true));
      const data = result && result.logs;

      setLastRefreshedTime(new Date())
      if (Array.isArray(data)) {
        return data.map((l) => ({ ...l }));
      }else{
        return false
      }
    } catch (error) {
      console.error(error);
    }
  }, [logMap, dispatch])

  const fetchSsliceInfo = useCallback(async () => {
    try{
      const result = await dispatch(invokeGetService('ssliceInfo', {}, true));
      if(result.status){
        setSsliceInfo(result?.details)
        setDebugLevel(result?.details.logLevel)
      }
    }catch(error){
      console.error(error)
    }
  },[dispatch])

  const processLogLine = (line) => {

      const successRegex = /\[success\](.*?)\[\/success\]/gi;
      const errorRegex = /\[error\](.*?)\[\/error\]/gi;
      var bpidRegex = /<bpid>(.*?)<\/bpid>/;
      var quoteRegex = /"/gi;
      var colourCodeRegx = /\\u001b\[[0-9]{1,2}m/gi;
      var newLineRegex = /hbx_newline/gi;
      const initialDateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} ::/;
      const stripAnsi = /\x1B\[[0-9;]*m/g;
      // let pline = line.replace(bpidRegex, '').replace(quoteRegex, '').trim();

      const logStruct = {
        msg: line
      }

      let processedLine = line;
     
      processedLine = processedLine.replace(successRegex, '$1');
      processedLine = processedLine.replace(errorRegex, '$1');
      processedLine = processedLine.replace(bpidRegex, '');
      processedLine = processedLine.replace(quoteRegex, '');
      processedLine = processedLine.replace(colourCodeRegx, '');
      processedLine = processedLine.replace(newLineRegex, '\n');
      processedLine = processedLine.replace(initialDateRegex, '')
      processedLine = processedLine.replace(stripAnsi, '')

      logStruct.msg = processedLine.trim()
      return logStruct;
  };

  const fetchSimulationLogs = useCallback(async({batchId, search, from, to, logLevel, level}) => {
      const queries = {
        batchId
      }
      if(search) queries.search = search;
      if(from) queries.from = from;
      if(to) queries.to = to;
      if (logLevel) queries.loglevel = logLevel;
      if (level) queries.severity = level;
      const response = await dispatch(invokeGetService('simulationLogs', queries, true))

      return response?.logs.map(log => {
        const logDetails = processLogLine(log.log)
        return {
          ...log,
          msg: logDetails.msg,
          ts: log.ts ?? log.createdAt,
          level: log.severity
        }
      })
  },[ dispatch ])

  const sortValidations = (validations) => {
    try {
        if (screen === "transaction" || screen === "view") {
            const sortedItems = [...validations].sort((a, b) => {
                const aIsMatch =
                    a.mainEntityType === screen && a.mainEntityId === entityId;
                const bIsMatch =
                    b.mainEntityType === screen && b.mainEntityId === entityId;

                if (aIsMatch && !bIsMatch) return -1; // a comes first
                if (!aIsMatch && bIsMatch) return 1;  // b comes first
                return 0; // keep original order otherwise
            });

            return sortedItems;
        } else if (screen === "platform-schema") {
            const sortedItems = [...validations].sort((a, b) => {
                const aIsMatch = a.entityType === "routing_rule" && a.severity === "error";
                const bIsMatch = b.entityType === 'routing_rule' && a.severity === "error";

                if (aIsMatch && !bIsMatch) return -1; // a comes first
                if (!aIsMatch && bIsMatch) return 1;  // b comes first
                return 0; // keep original order otherwise
            });

            return sortedItems;
        }

        return validations;
    } catch (error) {
        console.error(`Filtering validations failed. ${error}`);
        return validations;
    }
  };

  const getTimestamp = (ts) => {
    // Runtime logs: no timezone
    if (!ts.endsWith("Z")) {
      ts = ts + "Z";
    }
  
    return Date.parse(ts);
  };
  
  const timeMicroseconds = (ts) => {
    const [datePart, fraction] = String(ts).split(".");
    const milliseconds = fraction.padEnd(3, "0").slice(0,3);
    const microseconds = fraction.slice(-3).padEnd(3,"0");

    const time = new Date(datePart).getTime();
    return (time + parseInt(milliseconds)) * 1000 + parseInt(microseconds);
  }
  
  const getApiTimeFromFilter = (time) => {
    // All time: no time restriction
    if (time === "all" || time === null || time === undefined) {
      return false;
    }
  
    // Custom date range from LogTimeFilter
    if (time.type === "custom") {
      if (!time.start || !time.end) {
        return false;
      }
  
      return {
        from: new Date(time.start).toISOString(),
        to: new Date(time.end).toISOString(),
      };
    }
  
    // Preset duration in minutes
    if (typeof time === "number" && time > 0) {
      const now = new Date();
  
      return {
        from: new Date(now.getTime() - time * 60 * 1000).toISOString(),
        to: now.toISOString(),
      };
    }
  
    return false;
  };

  const fetchAndCombineLogs = useCallback(async (isRefresh) => {
    if(!isRefresh && !justPostSimulation) setLoadingLogs(true)
    if(justPostSimulation) {
      setTimeout(() => setJustPostSimulation(false), 1000)
      return false
    }
    
    const timeFilter = getApiTimeFromFilter(time);
    console.log({timeFilter})
    if(activeSources.has("Runtime") && activeSources.has("Simulation") && selectedSimulationRun){
      const LAG_MS = 5 * 1000; 
      const batchStart = timeFilter.from || new Date(
        new Date(selectedSimulationRun.createdAt).getTime() - LAG_MS
      );
      
      const batchEnd = timeFilter.to || new Date(
        new Date(selectedSimulationRun.updatedAt).getTime() + LAG_MS
      );

      let runtimeLogs = await fetchLogs(level, debouncedSearch, batchStart, batchEnd);
      const simulationLogs = await fetchSimulationLogs({batchId: selectedSimulationRun?._id, search: debouncedSearch, from: timeFilter?.from, to: timeFilter?.to, logLevel: logLevel, level: level})
      const mergedLogs = [...runtimeLogs, ...simulationLogs].sort((a, b) =>  {
        console.log(timeMicroseconds(a.ts), timeMicroseconds(b.ts))
        return timeMicroseconds(b.ts) - timeMicroseconds(a.ts)
      });

      setLogs(mergedLogs)
    }else if(activeSources.has("Simulation") && selectedSimulationRun){
      const simulationLogs = await fetchSimulationLogs({batchId: selectedSimulationRun?._id, search: debouncedSearch, from: timeFilter?.from, to: timeFilter?.to, logLevel: logLevel, level: level})
      setLogs(simulationLogs)
    }else if(!activeSources.has('Simulation')){
      let runtimeLogs = await fetchLogs(level, debouncedSearch, timeFilter?.from, timeFilter?.to)
      setLogs(runtimeLogs);
    }

    setLoadingLogs(false)
  },[level, debouncedSearch, time, selectedSimulationRun, activeSources, fetchSimulationLogs, fetchLogs, justPostSimulation])

  useEffect(() => {
    if(logMap?.handlerIdentifier && isSimulationRunning !== "live"){
      fetchAndCombineLogs();
    }else{
      setLogs([])
      setLoadingLogs(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logMap, level, time, debouncedSearch, selectedSimulationRun, activeSources, isSimulationRunning]);

  useEffect(() => {
    fetchSsliceInfo();
    if (simulationRunning) {
      setLogLevel(1);
    }
  },[open])

  useEffect(() => {
    if(isSimulationRunning){
      if(isSimulationRunning === "live"){
        setJustPostSimulation(true);
      }
      activeSources.clear();
      activeSources.add('Runtime')
      activeSources.add('Simulation')
    }else if((activeSources.size === 0 && open) || validatorStatus){
      if(counts['Blueprint Validator'] > 0){
        activeSources.clear()
        activeSources.add('Blueprint Validator')
      }else{
        activeSources.add('Runtime')
      }
    }
  },[isSimulationRunning, validatorStatus, activeSources, counts, open])

  useEffect(() => {
    fetchValidations(debouncedSearch, level);
  },[triggerValidation, fetchValidations, debouncedSearch, level, open])

  useEffect(() => {
    const handleLogs = (logs=[]) => {
      setLastRefreshedTime(new Date())
      if(logs.length > 0) setLoadingLogs(false)
      logs = logs.map(log => {
        if(log.src === "Simulation"){
          const logDetails = processLogLine(log.log)
          return {
            ...log,
            msg: logDetails.msg,
            ts: log.ts ?? log.createdAt,
            level: log.severity
          }
        }
        return log;
      })
      setLogs(prev => [...logs, ...prev])
    }
    if(socket.connected){
      socket.on(EVENTS.RUNTIME_LOG, handleLogs)
    }else{
      socket.once("connect", ()=>{
        socket.on(EVENTS.RUNTIME_LOG, handleLogs)
      })
    }

    return () => {
      socket.off('connect', () => {
        socket.off(EVENTS.RUNTIME_LOG, handleLogs)
      })
    }
  }, [])

  useEffect(() => {
    if(activeSources.has('Blueprint Validator')){
      setTableData(sortValidations(validatorLogs))
    }else if(activeSources.has('Runtime') && activeSources.has('Simulation')){
      setTableData(logs)
    }else if(activeSources.has('Runtime')){
      setTableData(logs.filter(log => log.src === "Runtime"))
    }else if(activeSources.has('Simulation')){
      setTableData(logs.filter(log => log.src === "Simulation"))
    }
  },[activeSources, logs, validatorLogs, setTableData])

  const toggleSource = useCallback((s) => {
    setActiveSources((prev) => computeNextSources(prev, s));
  }, []);

  function handleRefresh() {
    setRefreshSpin(true);
    setTimeout(() => setRefreshSpin(false), 500);
    fetchSsliceInfo();
    if(activeSources.has('Blueprint Validator')){
      fetchValidations();
    }else{
      fetchAndCombineLogs(true);
    }
  }

  async function handleDebugLevelChange(e) {
    const oldValue = debugLevel;
    const value = e.target.value;
    setDebugLevel(value);
    const result = await dispatch(invokePostService('changeSsliceLogLevel', {logLevel: value}, {}, true))

    if(!result.status){
      setDebugLevel(oldValue)
    }
  }

  async function handleRestartSlice() {
    setRestarting(true);
    try{
      const response = await dispatch(invokePostService('restartSslice', {}, {}, true));

      if(!response.status){
        console.error("Restart failed")
      }
      socket.emit(EVENTS.RESET_POLL_TIMER, {blueprintId})
    }catch(error){

    }finally{
      setRestarting(false)
    }
  }

  const handleLevelChange = (e) => {
    e.stopPropagation();
    setLevel(e.target.value);
    socket.emit(EVENTS.UPDATE_LOG_FILTER, {level: e.target.value});
    socket.emit(EVENTS.RESET_POLL_TIMER, {blueprintId})
  };

  const onLogLevelChange = async (e) => {
    e.stopPropagation();
    setLogLevel(e.target.value);
    if(activeSources.has("Simulation")){
      socket.emit(EVENTS.UPDATE_LOG_FILTER, {loglevel: e.target.value});
      socket.emit(EVENTS.RESET_POLL_TIMER, {blueprintId})
      const timeFilter = getApiTimeFromFilter(time);
      const simulationLogs = await fetchSimulationLogs({batchId: selectedSimulationRun?._id, search: debouncedSearch, from: timeFilter?.from, to: timeFilter?.to, logLevel: e.target.value, level: level})
      setLogs(simulationLogs)
    }
  };

  const handleClearRuntimeLogs = async (e) => {
    e.stopPropagation();
    setClearingRuntimeLogs(true);
    const result = await dispatch(invokePostService('clearRuntimeLogs', {}, {}, true))
    if(result.status){
      socket.emit(EVENTS.RESET_POLL_TIMER, {blueprintId})
      setLogs(logs => logs.filter(log => log.src !== "Runtime"))
    }
    setClearingRuntimeLogs(false);
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      classes={{ paper: classes.paper }}
      PaperProps={{id: "logs-modal"}}  
    >
      <LogsHeader onClose={onClose} />

      {activeSources.has('Runtime') && (
        <RuntimeConfigPanel
          debugLevel={debugLevel}
          onDebugLevelChange={handleDebugLevelChange}
          sliceInfo={ssliceInfo}
          restarting={restarting}
          onRestartSlice={handleRestartSlice}
        />
      )}

      <LogsToolbar
        activeSources={activeSources}
        onToggleSource={toggleSource}
        search={search}
        onSearchChange={setSearch}
        level={level}
        onLevelChange={handleLevelChange}
        time={time}
        onTimeChange={setTime}
        showTimeFilter={showTimeFilter}
        counts={counts}
        selectedSimulationRun={selectedSimulationRun}
        setSelectedSimulationRun={setSelectedSimulationRun}
        onRefresh={handleRefresh} 
        refreshSpin={refreshSpin}
        isSimulationRunning={isSimulationRunning}
        handleClearRuntimeLogs={handleClearRuntimeLogs}
        clearingRuntimeLogs={isClearingRuntimeLogs}
        onLogLevelChange={onLogLevelChange}
        logLevel={logLevel}
      />
      
      <LogsTable 
        logs={tableData} 
        activeSources={activeSources}
        closeModal={onClose}
        isLoading={(activeSources.has('Blueprint Validator') && isLoading) || (!activeSources.has('Blueprint Validator') && loadingLogs)}
        query={debouncedSearch}
        time={time}
      />

      <div className={classes.footer}>
        {activeSources.has("Runtime") && (
          <LastRefreshedTime lastRefreshedTime={lastRefreshedTime} />
        )}

        {validatorStatus && (
          <div className={classes.footerActions}>
            <button
              className={classes.cancelButton}
              onClick={() => {
                onClose();
                dispatch(toggleValidator(false));
              }}
            >
              Cancel
            </button>
            
            <button
              className={classes.proceedButton}
              onClick={() => {
                onClose();
                dispatch(toggleValidator(false));
                dispatch(toggleDeployment(true));
              }}
            >
              Proceed & Deploy
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}